/* eslint-disable react/prop-types */
/**
 * ID64 — Horizon Scan "Search Parameters" tab.
 * - View-only list of Company Search Parameters (from settings).
 * - Full user-search management via the embedded UserSearch (add/edit/delete pop-ups).
 * - "Display Search Results" tick-boxes (with an "All" row, checked by default) that filter the
 *   Dashboard scatter + Themes list to only themes produced by the selected parameters.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Checkbox, Chip, FormControlLabel, Link, Paper } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { protectedResources } from "authConfig";
import UserSearch from "./UserSearch";

const API = protectedResources.apiTodoList.endpoint;
const CRIT_COLOR = { Low: "#757575", Medium: "#ed6c02", High: "#d32f2f", Extreme: "#b71c1c" };

export default function SearchParametersPanel({ execute, selectedIds, onChangeSelected }) {
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const [companyParams, setCompanyParams] = useState([]);
  const [userSearches, setUserSearches] = useState([]);

  const loadCompany = useCallback(() => {
    executeRef
      .current("GET", `${API}/companySearchParameters/list`)
      .then((r) => {
        if (r?.success && Array.isArray(r.data)) setCompanyParams(r.data);
      })
      .catch(() => {});
  }, []);

  const loadUser = useCallback((rowsFromChild) => {
    if (Array.isArray(rowsFromChild)) {
      setUserSearches(rowsFromChild);
      return;
    }
    executeRef
      .current("GET", `${API}/anticipateSetting/list`)
      .then((r) => {
        if (r?.success && Array.isArray(r.data)) setUserSearches(r.data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadCompany();
    loadUser();
  }, [loadCompany, loadUser]);

  const companyItems = companyParams.map((p) => ({
    id: String(p.id),
    name: p.name,
    kind: "Company",
    criticality: p.criticality || "",
    description: p.description || "",
    sourceLink: p.sourceLink || "",
  }));
  const userItems = userSearches.map((u) => ({
    id: String(u.id),
    name: u.name,
    kind: "User",
    keyword: u.keyword || "",
  }));
  const allIds = [...companyItems, ...userItems].map((i) => i.id);

  const allSelected = selectedIds === null; // null = All
  const isChecked = (id) => allSelected || (selectedIds || []).includes(id);

  const toggleAll = () => {
    onChangeSelected(allSelected ? [] : null);
  };

  const toggleOne = (id) => {
    // From "All", switch to an explicit list of everything-but-this.
    const base = allSelected ? [...allIds] : [...(selectedIds || [])];
    const next = base.includes(id) ? base.filter((x) => x !== id) : [...base, id];
    onChangeSelected(next.length === allIds.length ? null : next);
  };

  const rowSx = {
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    py: 0.5,
    borderBottom: "1px solid rgba(0,0,0,0.05)",
  };

  // ID159 — fixed-height, independently-scrollable field for each parameter list so a long list
  // never pushes the other list or the "Manage" section off-screen. Company gets more real estate.
  const scrollBoxSx = (maxHeight) => ({
    maxHeight,
    overflowY: "auto",
    pr: 0.5,
    border: "1px solid rgba(0,0,0,0.08)",
    borderRadius: 1,
    // Slim, on-brand scrollbar so the two fields read as fixed panes.
    "&::-webkit-scrollbar": { width: 8 },
    "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(82,0,155,0.35)", borderRadius: 4 },
  });

  return (
    <MDBox>
      <MDTypography variant="h6" sx={{ color: "#52009b" }} gutterBottom>
        Search Parameters
      </MDTypography>
      <MDTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
        Tick <strong>Display Search Results</strong> to choose which parameters feed the Dashboard
        and Themes views. With <strong>All</strong> selected (default) you see every theme.
      </MDTypography>

      {/* ── Display Search Results filter ── */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, boxShadow: "none" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
            borderBottom: "2px solid rgba(82,0,155,0.15)",
            pb: 0.5,
          }}
        >
          <MDTypography variant="subtitle2" fontWeight={700} sx={{ color: "#52009b" }}>
            Display Search Results
          </MDTypography>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={allSelected}
                onChange={toggleAll}
                sx={{ color: "#52009b", "&.Mui-checked": { color: "#52009b" } }}
              />
            }
            label={<MDTypography variant="button">All</MDTypography>}
          />
        </Box>

        {/* Company Search parameters (view-only) */}
        <MDTypography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ display: "block", mt: 1, mb: 0.5 }}
        >
          Company Search parameters
        </MDTypography>
        {companyItems.length === 0 ? (
          <MDTypography variant="caption" color="text.disabled">
            None configured.
          </MDTypography>
        ) : (
          <Box sx={scrollBoxSx(300)}>
            {companyItems.map((it) => (
              <Box key={it.id} sx={rowSx}>
                <Checkbox
                  size="small"
                  checked={isChecked(it.id)}
                  onChange={() => toggleOne(it.id)}
                  sx={{ p: 0.5, color: "#52009b", "&.Mui-checked": { color: "#52009b" } }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                    <MDTypography variant="button" fontWeight="bold">
                      {it.name}
                    </MDTypography>
                    <Chip label="Company" size="small" sx={{ height: 18, fontSize: "0.65rem" }} />
                    {it.criticality && (
                      <Chip
                        label={it.criticality}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.65rem",
                          color: "#fff",
                          bgcolor: CRIT_COLOR[it.criticality] || "#757575",
                        }}
                      />
                    )}
                  </Box>
                  {it.description && (
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      {it.description}
                    </MDTypography>
                  )}
                  {it.sourceLink && (
                    <Link
                      href={
                        /^https?:\/\//i.test(it.sourceLink)
                          ? it.sourceLink
                          : `https://${it.sourceLink}`
                      }
                      target="_blank"
                      rel="noopener"
                      sx={{ fontSize: "0.72rem" }}
                    >
                      View data source ↗
                    </Link>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* User Search parameters */}
        <MDTypography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ display: "block", mt: 2, mb: 0.5 }}
        >
          User Search parameters
        </MDTypography>
        {userItems.length === 0 ? (
          <MDTypography variant="caption" color="text.disabled">
            None yet — add one below.
          </MDTypography>
        ) : (
          <Box sx={scrollBoxSx(180)}>
            {userItems.map((it) => (
              <Box key={it.id} sx={rowSx}>
                <Checkbox
                  size="small"
                  checked={isChecked(it.id)}
                  onChange={() => toggleOne(it.id)}
                  sx={{ p: 0.5, color: "#52009b", "&.Mui-checked": { color: "#52009b" } }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                    <MDTypography variant="button" fontWeight="bold">
                      {it.name}
                    </MDTypography>
                    <Chip
                      label="User"
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ height: 18, fontSize: "0.65rem" }}
                    />
                  </Box>
                  {it.keyword && (
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block" }}
                    >
                      {it.keyword}
                    </MDTypography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* ── Manage user search parameters (full functionality via embedded UserSearch) ── */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, boxShadow: "none" }}>
        <MDTypography variant="subtitle2" fontWeight={700} sx={{ color: "#52009b", mb: 1 }}>
          Manage user search parameters
        </MDTypography>
        <UserSearch embedded onChanged={loadUser} />
      </Paper>
    </MDBox>
  );
}
